import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Card, Form, Button } from 'react-bootstrap';
import {
  Formik,
  Field,
  ErrorMessage,
  FormikHelpers,
  useFormikContext,
} from 'formik';
import {
  METALS,
  SLIDER_RULES,
  Frequency,
  AllocationMode,
  MetalKey,
  MIN_AMOUNTS,
  MULTI_DEFAULTS,
  FAQS,
  ROUTE_CONFIG_MAP,
  PaymentRule,
  PAYMENT_RULES,
} from '../constants/sip.constants';
import CouponModal from './CouponModal';
import SipSummaryModal from './SipSummaryModal';
import FAQAccordion from 'shared/common/faq';
import ConfirmActionModal from './ConfirmActionModal';
import { useLocation } from 'react-router-dom';
import { fireCouponConfetti } from 'utils/confetti';
import SipHttp from '../services/sip.service';
import { userStore } from 'store/UserStore';
import { toast } from 'react-toastify';
import '../styles/CreateNewSip.css';

interface FormValues {
  frequency: Frequency;
  investmentAmount: number | '';
  sipName: string;
  metals: Record<MetalKey, { amount: number | '' }>;
  sipDuration: number;
  startDate: string;
  paymentMethod: 'payemandate' | 'paymanual' | '';
  emandateType: 'withMandateUPI' | 'withMandate' | '';
  couponApplied: boolean;
  couponName: string;
  autoRenew: boolean;
  acceptTerms: boolean;
  totalAmount?: number;
}

interface SipFormikEffectsProps {
  allocationMode: AllocationMode;
  selectedMetal: MetalKey;
  setFieldValue: (field: string, value: any) => void;
  singleAmountByFrequency: Partial<Record<Frequency, number>>;
  goldSilverByFrequency: Partial<
    Record<Frequency, { amount: number; goldPercent: number }>
  >;
  multiAmountByFrequency: Partial<Record<Frequency, Record<MetalKey, number>>>;
  sipNameByFrequency: Partial<Record<Frequency, string>>;
  setGoldPercent: (val: number) => void;
}

const getInitialValues = (
  allocationMode: AllocationMode,
  frequency: Frequency
): FormValues => {
  const freq = frequency;
  const defaultAmt = MIN_AMOUNTS.single.default[freq];
  if (allocationMode === 'goldSilver') {
    return {
      frequency: freq,
      sipName: '',
      investmentAmount: MIN_AMOUNTS.goldSilver.default[freq],
      sipDuration: 12,
      startDate: '',
      paymentMethod: '',
      emandateType: '',
      couponApplied: false,
      couponName: '',
      autoRenew: true,
      acceptTerms: false,

      metals: {
        gold: { amount: '' },
        silver: { amount: '' },
        platinum: { amount: '' },
        palladium: { amount: '' },
      },
    };
  }
  if (allocationMode === 'multi') {
    const d = MULTI_DEFAULTS[freq];
    return {
      frequency: freq,
      sipName: '',
      investmentAmount: '',
      sipDuration: 12,
      startDate: '',
      paymentMethod: '',
      emandateType: '',
      couponApplied: false,
      couponName: '',
      autoRenew: true,
      acceptTerms: false,

      metals: {
        gold: { amount: d.gold },
        silver: { amount: d.silver },
        platinum: { amount: d.platinum },
        palladium: { amount: d.palladium },
      },
    };
  }
  return {
    frequency: freq,
    sipName: '',
    investmentAmount: '',
    sipDuration: 12,
    startDate: '',
    paymentMethod: '',
    emandateType: '',
    couponApplied: false,
    couponName: '',
    autoRenew: true,
    acceptTerms: false,
    metals: {
      gold: { amount: defaultAmt },
      silver: { amount: defaultAmt },
      platinum: { amount: defaultAmt },
      palladium: { amount: defaultAmt },
    },
  };
};

const getPaymentRule = (
  allocationMode: AllocationMode,
  frequency: Frequency
): PaymentRule => {
  return (
    PAYMENT_RULES[`${allocationMode}_${frequency}`] ?? {
      methods: ['payemandate', 'paymanual'],
      defaultMethod: 'payemandate',
      emandateTypes: ['withMandateUPI', 'withMandate'],
      defaultEmandateType: 'withMandateUPI',
    }
  );
};

const getStartDateLimits = (
  paymentMethod: 'payemandate' | 'paymanual' | '',
  frequency: Frequency
) => {
  const today = new Date();
  let minDate = new Date();
  let maxDate = new Date();

  if (paymentMethod === 'payemandate') {
    const offset = frequency === 'daily' ? 2 : 5;
    minDate.setDate(today.getDate() + offset);
  } else {
    minDate = today;
  }

  maxDate = new Date(minDate);
  maxDate.setMonth(minDate.getMonth() + 1);

  const formatDate = (d: Date) => d.toISOString().split('T')[0];

  return {
    min: formatDate(minDate),
    max: formatDate(maxDate),
    default: formatDate(minDate),
  };
};

const SipFormikEffects = ({
  allocationMode,
  selectedMetal,
  setFieldValue,
  singleAmountByFrequency,
  goldSilverByFrequency,
  multiAmountByFrequency,
  sipNameByFrequency,
  setGoldPercent,
}: SipFormikEffectsProps) => {
  const { values } = useFormikContext<FormValues>();

  const prevFrequencyRef = useRef<Frequency>(values.frequency);
  const prevAllocationModeRef = useRef<AllocationMode>(allocationMode);

  useEffect(() => {
    const freqChanged = prevFrequencyRef.current !== values.frequency;

    if (allocationMode === 'single') {
      const amount =
        singleAmountByFrequency[values.frequency] ??
        MIN_AMOUNTS.single.default[values.frequency];

      if (freqChanged || prevAllocationModeRef.current !== 'single') {
        METALS.forEach((m) => {
          setFieldValue(`metals.${m.key}.amount`, amount);
        });
      }
    }

    if (allocationMode === 'goldSilver') {
      const stored = goldSilverByFrequency[values.frequency];

      const shouldInitialize =
        freqChanged || prevAllocationModeRef.current !== 'goldSilver';

      if (shouldInitialize) {
        const amount =
          stored?.amount ?? MIN_AMOUNTS.goldSilver.default[values.frequency];

        const percent = stored?.goldPercent ?? 50;

        setFieldValue('investmentAmount', amount);
        setGoldPercent(percent);

        const goldAmt = Math.round((amount * percent) / 100);

        setFieldValue('metals.gold.amount', goldAmt);
        setFieldValue('metals.silver.amount', amount - goldAmt);
      }
    }

    if (allocationMode === 'multi') {
      const stored = multiAmountByFrequency[values.frequency];
      const defaults = MULTI_DEFAULTS[values.frequency];

      const shouldInitialize =
        freqChanged || prevAllocationModeRef.current !== 'multi';

      if (shouldInitialize) {
        METALS.forEach((m) => {
          setFieldValue(
            `metals.${m.key}.amount`,
            stored?.[m.key] ?? defaults[m.key]
          );
        });
      }
    }

    prevFrequencyRef.current = values.frequency;
    prevAllocationModeRef.current = allocationMode;

  }, [
    values.frequency,
    allocationMode,
    selectedMetal,
    singleAmountByFrequency,
    goldSilverByFrequency,
    multiAmountByFrequency,
    setFieldValue,
    setGoldPercent,
  ]);

  useEffect(() => {
    const stored = sipNameByFrequency[values.frequency];
    if (stored !== undefined) setFieldValue('sipName', stored);
  }, [values.frequency, sipNameByFrequency, setFieldValue]);

  return null;
};

const StartDateEffect = () => {
  const { values, setFieldValue } = useFormikContext<FormValues>();

  useEffect(() => {
    if (!values.paymentMethod) return;

    const { default: nextDate } = getStartDateLimits(
      values.paymentMethod,
      values.frequency
    );

    setFieldValue('startDate', nextDate);
  }, [values.paymentMethod, values.frequency, setFieldValue]);

  return null;
};

const PaymentRuleEffect = ({
  allocationMode,
}: {
  allocationMode: AllocationMode;
}) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();

  useEffect(() => {
    const rule = getPaymentRule(allocationMode, values.frequency);

    const isSingleUPIRule =
      rule.methods.length === 1 &&
      rule.methods[0] === 'payemandate' &&
      rule.emandateTypes &&
      rule.emandateTypes.length === 1;

    if (isSingleUPIRule) {
      if (values.paymentMethod !== 'payemandate') {
        setFieldValue('paymentMethod', 'payemandate');
      }
      if (values.emandateType !== rule.emandateTypes?.[0]) {
        setFieldValue('emandateType', rule.emandateTypes?.[0]);
      }
      return;
    }

    if (!values.paymentMethod || !rule.methods.includes(values.paymentMethod)) {
      setFieldValue('paymentMethod', rule.defaultMethod);
    }

    if (rule.defaultMethod === 'payemandate') {
      if (
        !values.emandateType ||
        (rule.emandateTypes &&
          !rule.emandateTypes.includes(values.emandateType))
      ) {
        setFieldValue('emandateType', rule.defaultEmandateType ?? '');
      }
    } else {
      if (values.emandateType !== '') {
        setFieldValue('emandateType', '');
      }
    }
  }, [
    allocationMode,
    values.frequency,
    values.paymentMethod,
    values.emandateType,
    setFieldValue,
  ]);

  return null;
};

const CreateSip = () => {
  const location = useLocation();
  
  const locationState = location.state as {
    sipAmount?: number;
    sipInterval?: 'daily' | 'weekly' | 'monthly';
    sipItemCode?: string;
    sipMetalType?: string;
  } | null;

  const sipCardRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);

  const [customerId] = userStore((state: any) => [state.customerId]);


  const [goldPercent, setGoldPercent] = useState(50);
  const [showDetails, setShowDetails] = useState(false);

  const [singleAmountByFrequency, setSingleAmountByFrequency] = useState<Partial<Record<Frequency, number>>>({
    ...(locationState?.sipAmount ? {
      daily: locationState.sipAmount,
      weekly: locationState.sipAmount,
      monthly: locationState.sipAmount,
    } : {})
  });

  const [goldSilverByFrequency, setGoldSilverByFrequency] = useState<
    Partial<Record<Frequency, { amount: number; goldPercent: number }>>
  >({});
  const [multiAmountByFrequency, setMultiAmountByFrequency] = useState<
    Partial<Record<Frequency, Record<MetalKey, number>>>
  >({});
  const [sipNameByFrequency, setSipNameByFrequency] = useState<
    Partial<Record<Frequency, string>>
  >({});

  const [showCouponModal, setShowCouponModal] = useState(false);
  const [showRemoveCouponModal, setShowRemoveCouponModal] = useState(false);

  const [showSummary, setShowSummary] = useState(false);
  const [finalPayload, setFinalPayload] = useState<FormValues | null>(null);

  const [coupons, setCoupons] = useState<any>([]);
  const [couponErrors, setCouponErrors] = useState<any[]>([]);

  const routeConfig =
    ROUTE_CONFIG_MAP[location.pathname] ??
    ROUTE_CONFIG_MAP['/sip'];

  const metalFromState = useMemo((): MetalKey => {
    const type = locationState?.sipMetalType?.toLowerCase();
    if (type === 'au') return 'gold';
    if (type === 'ag') return 'silver';
    if (type === 'pt') return 'platinum';
    if (type === 'pd') return 'palladium';
    return 'silver'; // fallback
  }, [locationState]);


  const [allocationMode, setAllocationMode] = useState<AllocationMode>(
    routeConfig.allocationMode
  );

  const [selectedMetal, setSelectedMetal] = useState<MetalKey>(
    locationState?.sipMetalType ? metalFromState : (routeConfig.defaultMetal ?? 'silver')
  );

  const [selectedFrequency, setSelectedFrequency] = useState<Frequency>(
    (locationState?.sipInterval as Frequency) ?? routeConfig.defaultFrequency
  );

  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return {
      investment: Number(params.get("investment")) || null,
      duration: Number(params.get("duration")) || null,
      type: params.get("type") || null,
    };
  }, [location.search]);



  // const initialValues = useMemo(
  //   () => getInitialValues(allocationMode, selectedFrequency),
  //   [allocationMode, selectedFrequency]
  // );


  const initialValues = useMemo(() => {
    const base = getInitialValues(allocationMode, selectedFrequency);

    const incomingAmount = locationState?.sipAmount ?? queryParams.investment;

    if (incomingAmount) {
      base.investmentAmount = incomingAmount;
      METALS.forEach((m) => {
        base.metals[m.key].amount = m.key === selectedMetal ? incomingAmount : 0;
      });
    }

    if (queryParams.duration) {
      base.sipDuration = queryParams.duration;
    }

    return base;
  }, [allocationMode, selectedFrequency, queryParams, selectedMetal, locationState]);


  const validate = useCallback(
    (values: FormValues) => {
      const errors: any = {};
      const freq = values.frequency;
      const paymentRule = getPaymentRule(allocationMode, values.frequency);

      const isSinglePaymentMethod =
        paymentRule.methods.length === 1;

      const isSingleEmandateType =
        paymentRule.emandateTypes &&
        paymentRule.emandateTypes.length === 1;

      if (allocationMode === 'single') {
        const amt = Number(values.metals[selectedMetal]?.amount);
        const min = MIN_AMOUNTS.single.min[freq];
        if (!amt || amt < min)
          errors.metals = { [selectedMetal]: { amount: `Minimum ₹${min}` } };
        else if (amt % 1 !== 0)
          errors.metals = {
            [selectedMetal]: { amount: 'Decimals not allowed' },
          };
      }

      if (allocationMode === 'goldSilver') {
        const amt = Number(values.investmentAmount);
        const min = MIN_AMOUNTS.goldSilver.min[freq];
        if (!amt || amt < min) errors.investmentAmount = `Minimum ₹${min}`;
        else if (amt % 10 !== 0)
          errors.investmentAmount =
            'Amount must be in multiples of ₹10 and minimum ₹40';
        const rules = SLIDER_RULES[freq];
        if (
          rules.operationalMin !== undefined &&
          (goldPercent < rules.operationalMin ||
            goldPercent > rules.operationalMax!)
        )
          errors.goldSilverRatio = `Gold allocation must be between ${rules.operationalMin}% and ${rules.operationalMax}%`;
      }

    
      if (allocationMode === 'multi') {
        let total = 0;
        let perMetalError = false;

        const selectedMetalsCount = METALS.filter(
          (m) => Number(values.metals[m.key]?.amount) > 0
        ).length;

        if (selectedMetalsCount < 2) {
          errors.totalAmount = 'Please enter amounts for at least 2 metals';
        }

        METALS.forEach((m) => {
          const amt = Number(values.metals[m.key]?.amount);
          total += amt || 0;
          if (amt && amt < 10) {
            errors.metals ??= {};
            errors.metals[m.key] = { amount: 'Minimum ₹10' };
            perMetalError = true;
          }
          if (amt % 1 !== 0) {
            errors.metals ??= {};
            errors.metals[m.key] = { amount: 'Decimals not allowed' };
            perMetalError = true;
          }
        });

        if (
          !perMetalError &&
          selectedMetalsCount >= 2 &&
          total < MULTI_DEFAULTS[freq].minTotalValue
        )
          errors.totalAmount = `Total must be at least ₹${MULTI_DEFAULTS[freq].minTotalValue}`;
      }

      if (showDetails && (!values.sipDuration || values.sipDuration <= 0)) {
        errors.sipDuration = 'SIP Duration is required';
      }

      if (showDetails) {
        if (!values.startDate) {
          errors.startDate = 'Start Date is required';
        } else {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const selectedDate = new Date(values.startDate);
          selectedDate.setHours(0, 0, 0, 0);

          let minDate = new Date(today);
          let maxDate = new Date(today);

          if (values.paymentMethod === 'payemandate') {
            const offsetDays = values.frequency === 'daily' ? 2 : 5;

            minDate.setDate(today.getDate() + offsetDays);
            maxDate = new Date(minDate);
            maxDate.setMonth(minDate.getMonth() + 1);

            if (selectedDate < minDate) {
              errors.startDate =
                values.frequency === 'daily'
                  ? 'For Daily SIP with e-andate, start date must be at least 2 days from today'
                  : 'For Weekly/Monthly SIP with Autopay, start date must be at least 5 days from today';
            } else if (selectedDate > maxDate) {
              errors.startDate =
                'Start Date cannot be more than 1 month from the allowed start date';
            }
          }

          if (values.paymentMethod === 'paymanual') {
            maxDate.setMonth(today.getMonth() + 1);

            if (selectedDate < today) {
              errors.startDate = 'Start Date cannot be before today';
            } else if (selectedDate > maxDate) {
              errors.startDate =
                'Start Date cannot be more than 1 month from today';
            }
          }
        }
      }

      if (
        showDetails &&
        !isSinglePaymentMethod &&
        !values.paymentMethod
      ) {
        errors.paymentMethod = 'Please select a payment method';
      }


      if (
        showDetails &&
        !isSinglePaymentMethod &&
        values.paymentMethod === 'payemandate' &&
        !values.emandateType
      ) {
        errors.emandateType = 'Please select emandate type';
      }

      if (showDetails && !values.acceptTerms) {
        errors.acceptTerms = 'You must accept terms & conditions';
      }

      return errors;
    },
    [allocationMode, selectedMetal, goldPercent, showDetails]
  );

  const generateSipName = (
    values: FormValues,
    allocationMode: AllocationMode
  ): string => {
    const freq = values.frequency.toUpperCase();

    let totalAmount = 0;

    if (allocationMode === 'goldSilver') {
      totalAmount = Number(values.investmentAmount) || 0;
    } else {
      totalAmount = Object.values(values.metals).reduce(
        (sum, m) => sum + (Number(m.amount) || 0),
        0
      );
    }

    const activeMetals = Object.entries(values.metals)
      .filter(([, v]) => Number(v.amount) > 0)
      .map(([k]) => k.toUpperCase());

    let allocationPart = '';

    if (activeMetals.length === 1) {
      allocationPart = activeMetals[0];
    } else if (activeMetals.length === 2) {
      allocationPart = activeMetals.join('_');
    } else {
      allocationPart = 'MULTI_METAL';
    }

    return `${freq}_SIP_${totalAmount}_${allocationPart}`;
  };

  const getCoupons = async () => {
    try {
      const res = await SipHttp.getDiscountCoupons(customerId);
      const coupons = res?.data?.data;
      setCoupons(coupons);
    } catch (error) {
      console.error('Coupon fetch error:', error);
      toast.error('Failed to load coupons');
    }
  };

  const handleStartSip = async (
    validateForm: FormikHelpers<FormValues>['validateForm'],
    setTouched: FormikHelpers<FormValues>['setTouched']
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length) {
      setTouched(
        {
          frequency: true,
          sipName: true,
          investmentAmount: true,
          metals: METALS.reduce(
            (acc, m) => ({ ...acc, [m.key]: { amount: true } }),
            {}
          ),
          startDate: true,
          sipDuration: true,
          paymentMethod: true,
          emandateType: true,
          acceptTerms: true,
        },
        true
      );
      return;
    }

    setShowDetails(true);

    const yOffset = -20;
    const y =
      sipCardRef.current!.getBoundingClientRect().top +
      window.pageYOffset +
      yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const handleCreateSip = (values: FormValues) => {
    let updatedValues = { ...values };

    if (allocationMode === 'single') {
      const amt = Number(values.metals[selectedMetal]?.amount) || 0;
      updatedValues = {
        ...updatedValues,
        investmentAmount: amt,
        metals: {
          [selectedMetal]: { amount: amt },
        } as any,
      };
    }

    if (allocationMode === 'goldSilver') {
      const total = Number(values.investmentAmount) || 0;
      const goldAmt = Math.round((total * goldPercent) / 100);
      updatedValues = {
        ...updatedValues,
        metals: {
          gold: { amount: goldAmt },
          silver: { amount: total - goldAmt },
          platinum: { amount: 0 },
          palladium: { amount: 0 },
        },
      };
    }

    if (allocationMode === 'multi') {
      const total = Object.values(values.metals).reduce(
        (sum, m) => sum + (Number(m.amount) || 0),
        0
      );
      updatedValues = {
        ...updatedValues,
        investmentAmount: total,
      };
    }

    if (!updatedValues.sipName?.trim()) {
      updatedValues.sipName = generateSipName(updatedValues, allocationMode);
    }

    if (updatedValues.paymentMethod === 'payemandate') {
      const totalAmount = Number(updatedValues.investmentAmount) || 0;
      const freq = updatedValues.frequency;
      const emandateType = updatedValues.emandateType;

      if (emandateType === 'withMandateUPI') {
        if (totalAmount > 14999) {
          toast.error(
            `UPI Autopay supports a maximum of ₹14,999 per ${freq} investment. Please reduce your amount or choose Net Banking / Debit Card.`
          );
          return;
        }
      }

      if (emandateType === 'withMandate') {
        if (
          (freq === 'weekly' || freq === 'monthly') &&
          totalAmount > 99999
        ) {
          toast.error(
            `Net Banking / Debit Card Autopay supports a maximum of ₹99,999 per ${freq} investment. Please reduce your amount.`
          );
          return;
        }
      }
    }

    setFinalPayload(updatedValues);
    setShowSummary(true);
  };

  const handleRedirectToPayment = () => {
    setShowSummary(false);
    console.log(finalPayload, 'Final Payload');
  };

  const handleValidateCoupon = async (
    code: string,
    values: FormValues,
    setFieldValue: any
  ) => {
    try {
      if (allocationMode !== 'single') {
        toast.error('Coupons are available only for Single Metal SIP');
        return;
      }

      const amount = Number(values.metals[selectedMetal]?.amount) || 0;

      if (!amount || amount <= 0) {
        toast.error('Please enter investment amount before applying coupon');
        return;
      }

      const payload = {
        metal: selectedMetal.toUpperCase(),
        orderAmount: amount,
        orderMetalQuantity: 0,
        sipFrequency: values.frequency,
        sipDuration: values.sipDuration,
        discountName: code,
      };

      const res = await SipHttp.validateDiscountCoupon(payload, customerId);
      const responseData = res?.data?.data;
      const responseErrors = res?.data?.errors;

      // Case 1: Coupon not found — { errors: [{ code: 400, title: "Coupon Not Found" }] }
      if (Array.isArray(responseErrors) && responseErrors.length > 0) {
        const errorTitle = responseErrors[0]?.title || 'Coupon not available';
        setCouponErrors([errorTitle]);
        setFieldValue('couponApplied', false);
        setFieldValue('couponName', code);
        return;
      }

      // Case 2: Coupon applied successfully
      if (responseData?.couponApplied) {
        const discount = responseData.validDiscounts?.[0];
        setCouponErrors([]);
        setFieldValue('couponApplied', true);
        setFieldValue('couponName', discount?.name || code);
        setFieldValue('couponId', discount?.id);
        toast.success('Coupon applied successfully');
        return;
      }

      if (Array.isArray(responseData)) {
        const errorMessages = responseData
          .map((obj: any) => Object.values(obj)[0])
          .filter(Boolean);
        setCouponErrors(errorMessages.length > 0 ? errorMessages : ['Coupon validation failed. Please try another one.']);
      } else {
        setCouponErrors(['Coupon validation failed. Please try another one.']);
      }

      setFieldValue('couponApplied', false);
      setFieldValue('couponName', code);
    } catch (error: any) {
      setCouponErrors(['Invalid coupon code. Please check and try again.']);
    }
  };

  useEffect(() => {
    if (showDetails && detailsRef.current) {
      const yOffset = -100;
      const y =
        detailsRef.current.getBoundingClientRect().top +
        window.pageYOffset +
        yOffset;

      window.scrollTo({ top: y, behavior: 'smooth' });

      const sipNameInput = detailsRef.current.querySelector<HTMLInputElement>(
        'input[name="sipName"]'
      );
      sipNameInput?.focus();
    }
  }, [showDetails]);

  useEffect(() => {
    const config =
      ROUTE_CONFIG_MAP[location.pathname] ??
      ROUTE_CONFIG_MAP['/sip'];

    setAllocationMode(config.allocationMode);

    // Only use route defaults if no locationState was provided
    if (!locationState?.sipInterval) {
      setSelectedFrequency(config.defaultFrequency);
    }

    if (!locationState?.sipMetalType) {
      setSelectedMetal(config.defaultMetal ?? 'silver');
    }
  }, [location.pathname]);


  useEffect(() => {
    getCoupons();
  }, []);

  return (
    <>
      <Container className='sip-wrapper' ref={sipCardRef}>
        <Card className='sip-card'>
          <h4 className='sip-title'>Create Your Metal SIP</h4>
          <p className='sip-subtitle'>
            Select how you want to invest in precious metals
          </p>

          <Formik
            // enableReinitialize
            initialValues={initialValues}
            validate={validate}
            onSubmit={handleCreateSip}
          >
            {({
              values,
              errors,
              handleSubmit,
              validateForm,
              setTouched,
              setFieldValue,
            }) => {
              const paymentRule = getPaymentRule(
                allocationMode,
                values.frequency
              );
              const isSinglePaymentMethod =
                paymentRule.methods.length === 1;


              const totalMetalAmount = (() => {
                if (allocationMode === 'single') {
                  const amt = values.metals[selectedMetal]?.amount;
                  return amt === '' || amt === undefined ? '' : amt;
                }

                if (allocationMode === 'multi') {
                  const nums = Object.values(values.metals)
                    .map((m) => m.amount)
                    .filter((a): a is number => typeof a === 'number');

                  return nums.length ? nums.reduce((a, b) => a + b, 0) : '';
                }

                return '';
              })();

              const sharedSingleAmount =
                allocationMode === 'single'
                  ? singleAmountByFrequency[values.frequency] ??
                  values.metals[selectedMetal]?.amount
                  : undefined;

              const goldAmount = Math.round(
                (Number(values.investmentAmount) * goldPercent) / 100
              );
              const silverAmount = Number(values.investmentAmount) - goldAmount;
              const sliderRules = SLIDER_RULES[values.frequency];

              useEffect(() => {
                if (values.couponApplied) {
                  fireCouponConfetti({ x: 0.5, y: 0.7 });
                }
              }, [values.couponApplied]);

              useEffect(() => {
                if (values.couponApplied) {
                  setFieldValue('couponApplied', false);
                  setFieldValue('couponName', '');
                }
              }, [
                values.frequency,
                values.sipDuration,
                values.metals[selectedMetal]?.amount,
                selectedMetal,
              ]);

              useEffect(() => {
                if (values.paymentMethod === 'paymanual') {
                  // Remove coupon
                  if (values.couponApplied) {
                    setFieldValue('couponApplied', false);
                    setFieldValue('couponName', '');
                    setCouponErrors([]);
                  }

                  // Disable auto renew
                  if (values.autoRenew) {
                    setFieldValue('autoRenew', false);
                  }
                }
              }, [values.paymentMethod]);

              return (
                <>
                  <StartDateEffect />

                  <PaymentRuleEffect allocationMode={allocationMode} />

                  <SipFormikEffects
                    allocationMode={allocationMode}
                    selectedMetal={selectedMetal}
                    setFieldValue={setFieldValue}
                    singleAmountByFrequency={singleAmountByFrequency}
                    goldSilverByFrequency={goldSilverByFrequency}
                    multiAmountByFrequency={multiAmountByFrequency}
                    sipNameByFrequency={sipNameByFrequency}
                    setGoldPercent={setGoldPercent}
                  />

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className='sip-field'>
                      <Form.Label>Investment Frequency</Form.Label>
                      <div className='frequency-group'>
                        {(['daily', 'weekly', 'monthly'] as Frequency[]).map(
                          (f) => (
                            <label key={f} className='radio-pill-frequency'>
                              <Field
                                type='radio'
                                name='frequency'
                                value={f}
                                onChange={(e: any) => {
                                  setSelectedFrequency(f);
                                  setFieldValue('frequency', f);
                                }}
                              />
                              {f}
                            </label>
                          )
                        )}
                      </div>
                      <ErrorMessage
                        name='frequency'
                        component='div'
                        className='error-text'
                      />
                    </Form.Group>

                    <Form.Group className='sip-field'>
                      <Form.Label>Allocation Strategy</Form.Label>
                      <div className='allocation-toggle'>
                        <button
                          type='button'
                          className={
                            allocationMode === 'single' ? 'active' : ''
                          }
                          onClick={() => setAllocationMode('single')}
                        >
                          Single Metal
                          <span>Invest in one metal</span>
                        </button>

                        <button
                          type='button'
                          className={
                            allocationMode === 'goldSilver' ? 'active' : ''
                          }
                          onClick={() => setAllocationMode('goldSilver')}
                        >
                          Gold + Silver
                          <span>Fixed ratio allocation</span>
                        </button>

                        <button
                          type='button'
                          className={allocationMode === 'multi' ? 'active' : ''}
                          onClick={() => setAllocationMode('multi')}
                        >
                          Multi Metal
                          <span>Choose multiple metals</span>
                        </button>
                      </div>
                    </Form.Group>

                    {(allocationMode === 'single' ||
                      allocationMode === 'multi') && (
                        <div className='allocation-box compact'>
                          <div className='section-label'>
                            Enter Investment Amount
                          </div>

                          <div className='metal-table-unified'>
                            {METALS.map((m) => {
                              const isSelected = selectedMetal === m.key;
                              const showInput =
                                allocationMode === 'multi' || isSelected;

                              return (
                                <div
                                  key={m.key}
                                  className={`metal-row-unified ${isSelected ? 'selected' : ''
                                    }`}
                                  onClick={() =>
                                    allocationMode === 'single' &&
                                    setSelectedMetal(m.key)
                                  }
                                >
                                  <div className='metal-left'>
                                    {allocationMode === 'single' && (
                                      <input
                                        type='radio'
                                        checked={isSelected}
                                        readOnly
                                      />
                                    )}
                                    <span>{m.label}</span>
                                  </div>

                                  {showInput && (
                                    <Field name={`metals.${m.key}.amount`}>
                                      {({ field, meta }) => (
                                        <div className='input-with-symbol-wrapper'>
                                          <div className='input-with-symbol'>
                                            <input
                                              {...field}
                                              type='number'
                                              step='1'
                                              min='0'
                                              placeholder='Amount'
                                              className='metal-input-unified'
                                              onKeyDown={(e) =>
                                                e.key === '.' &&
                                                e.preventDefault()
                                              }
                                              value={
                                                allocationMode === 'single'
                                                  ? sharedSingleAmount ?? ''
                                                  : field.value
                                              }
                                              onChange={(e) => {
                                                const valStr = e.target.value;
                                                const val =
                                                  valStr === ''
                                                    ? ''
                                                    : Number(valStr);

                                                if (allocationMode === 'single') {
                                                  // store once per frequency
                                                  setSingleAmountByFrequency(
                                                    (prev) => ({
                                                      ...prev,
                                                      [values.frequency]:
                                                        val === ''
                                                          ? undefined
                                                          : val,
                                                    })
                                                  );

                                                  // update ALL metals internally
                                                  METALS.forEach((metal) => {
                                                    setFieldValue(
                                                      `metals.${metal.key}.amount`,
                                                      val
                                                    );
                                                  });
                                                }

                                                if (allocationMode === 'multi') {
                                                  setFieldValue(
                                                    `metals.${m.key}.amount`,
                                                    val
                                                  );
                                                  setMultiAmountByFrequency(
                                                    (prev) => ({
                                                      ...prev,
                                                      [values.frequency]: {
                                                        ...(prev[
                                                          values.frequency
                                                        ] ?? {}),
                                                        [m.key]:
                                                          val === ''
                                                            ? undefined
                                                            : val,
                                                      },
                                                    })
                                                  );
                                                }
                                              }}
                                            />

                                            <div className='input-symbol'>₹</div>
                                          </div>

                                          {meta.touched && meta.error && (
                                            <div className='error-text'>
                                              {meta.error}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </Field>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          <Form.Group className='sip-field'>
                            <Form.Label>Total Investment Amount (₹)</Form.Label>
                            <Form.Control
                              disabled
                              value={
                                totalMetalAmount === '' ? '' : totalMetalAmount
                              }
                              placeholder='Enter investment amount'
                            />
                            {errors.totalAmount && (
                              <div className='error-text'>
                                {errors.totalAmount}
                              </div>
                            )}
                          </Form.Group>
                        </div>
                      )}

                    {allocationMode === 'goldSilver' && (
                      <div className='allocation-box'>
                        <Form.Group className='sip-field'>
                          <Form.Label>Investment Amount (₹)</Form.Label>
                          <Field name='investmentAmount'>
                            {({ field, meta }) => (
                              <>
                                <input
                                  {...field}
                                  type='number'
                                  step='10'
                                  min='0'
                                  onKeyDown={(e) =>
                                    e.key === '.' && e.preventDefault()
                                  }
                                  placeholder='Enter investment amount'
                                  onChange={(e) => {
                                    const valStr = e.target.value;
                                    const val =
                                      valStr === '' ? '' : Number(valStr);

                                    setFieldValue('investmentAmount', val);

                                    if (val !== '') {
                                      setGoldSilverByFrequency((prev) => ({
                                        ...prev,
                                        [values.frequency]: {
                                          amount: val,
                                          goldPercent,
                                        },
                                      }));

                                      const goldAmt = Math.round(
                                        (val * goldPercent) / 100
                                      );
                                      setFieldValue(
                                        'metals.gold.amount',
                                        goldAmt
                                      );
                                      setFieldValue(
                                        'metals.silver.amount',
                                        val - goldAmt
                                      );
                                    } else {
                                      setFieldValue('metals.gold.amount', '');
                                      setFieldValue('metals.silver.amount', '');
                                    }
                                  }}
                                  className='form-control active-input'
                                />

                                {meta.touched && meta.error && (
                                  <div className='error-text'>{meta.error}</div>
                                )}
                              </>
                            )}
                          </Field>
                        </Form.Group>

                        <div className='slider-header'>
                          <span>
                            Gold {goldPercent}% • ₹{goldAmount}
                          </span>
                          <span>
                            Silver {100 - goldPercent}% • ₹{silverAmount}
                          </span>
                        </div>

                        <input
                          type='range'
                          className='metal-slider'
                          min={sliderRules.min}
                          max={sliderRules.max}
                          step={sliderRules.step}
                          value={goldPercent}
                          style={{
                            background: `linear-gradient(
      to right,
      #99834D 0%,
      #99834D ${((goldPercent - sliderRules.min) /
                                (sliderRules.max - sliderRules.min)) *
                              100
                              }%,
      #e0e0e0 ${((goldPercent - sliderRules.min) /
                                (sliderRules.max - sliderRules.min)) *
                              100
                              }%,
      #e0e0e0 100%
    )`,
                          }}
                          onChange={(e) => {
                            let val = Number(e.target.value);

                            if (values.frequency === 'daily') {
                              if (val < 38) val = 25;
                              else if (val < 63) val = 50;
                              else val = 75;
                            }

                            const rules = SLIDER_RULES[values.frequency];
                            if (
                              rules.operationalMin !== undefined &&
                              val < rules.operationalMin
                            ) {
                              val = rules.operationalMin;
                            }
                            if (
                              rules.operationalMax !== undefined &&
                              val > rules.operationalMax
                            ) {
                              val = rules.operationalMax;
                            }

                            setGoldPercent(val);

                            setGoldSilverByFrequency((prev) => ({
                              ...prev,
                              [values.frequency]: {
                                amount: Number(values.investmentAmount),
                                goldPercent: val,
                              },
                            }));

                            const goldAmt = Math.round(
                              (Number(values.investmentAmount) * val) / 100
                            );
                            setFieldValue('metals.gold.amount', goldAmt);
                            setFieldValue(
                              'metals.silver.amount',
                              Number(values.investmentAmount) - goldAmt
                            );
                          }}
                        />

                        <ErrorMessage
                          name='goldSilverRatio'
                          component='div'
                          className='error-text'
                        />
                      </div>
                    )}

                    {!showDetails && (
                      <Button
                        type='button'
                        className='sip-btn'
                        onClick={() => handleStartSip(validateForm, setTouched)}
                      >
                        Start SIP with this plan
                      </Button>
                    )}

                    {showDetails && (
                      <div ref={detailsRef}>
                        <Form.Group className='sip-field'>
                          <Form.Label>SIP Plan Name</Form.Label>
                          <Field name='sipName'>
                            {({ field }) => (
                              <input
                                {...field}
                                className='form-control active-input'
                                placeholder='Enter SIP plan name'
                                onChange={(e) => {
                                  field.onChange(e);
                                  setSipNameByFrequency((prev) => ({
                                    ...prev,
                                    [values.frequency]: e.target.value,
                                  }));
                                }}
                              />
                            )}
                          </Field>
                          <ErrorMessage
                            name='sipName'
                            component='div'
                            className='error-text'
                          />
                        </Form.Group>

                        <div className='sip-field duration-start-date'>
                          <Form.Group className='flex-field'>
                            <Form.Label>SIP Duration</Form.Label>
                            <Field
                              as='select'
                              name='sipDuration'
                              className='form-control select-input'
                            >
                              {[3, 6, 9, 12, 24].map((d) => (
                                <option
                                  key={d}
                                  value={d}
                                >{`${d} Months`}</option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name='sipDuration'
                              component='div'
                              className='error-text'
                            />
                          </Form.Group>

                          <Form.Group className='flex-field'>
                            <Form.Label>Start Date</Form.Label>
                            <Field name='startDate'>
                              {({ field, form }: any) => {
                                const { min, max } = getStartDateLimits(
                                  form.values.paymentMethod,
                                  form.values.frequency
                                );

                                return (
                                  <input
                                    {...field}
                                    type='date'
                                    className='form-control active-input'
                                    min={min}
                                    max={max}
                                  />
                                );
                              }}
                            </Field>

                            <ErrorMessage
                              name='startDate'
                              component='div'
                              className='error-text'
                            />
                          </Form.Group>
                        </div>


                        <Form.Group className='sip-field'>
                          {!isSinglePaymentMethod && (<>

                            <Form.Label>Payment Method</Form.Label>
                            <div className='payment-method'>
                              {paymentRule.methods.includes('payemandate') && (
                                <label className='radio-pill recommended'>
                                  <Field
                                    type='radio'
                                    name='paymentMethod'
                                    value='payemandate'
                                  />
                                  <span className='pill-text'>Autopay</span>
                                  {/* {paymentRule.methods.length === 1 && (
                                  <span className='recommended-badge'>
                                    Recommended
                                  </span>
                                )} */}
                                </label>
                              )}

                              {paymentRule.methods.includes('paymanual') && (
                                <label className='radio-pill'>
                                  <Field
                                    type='radio'
                                    name='paymentMethod'
                                    value='paymanual'
                                  />
                                  <span className='pill-text'>Pay Manually</span>
                                </label>
                              )}
                            </div>
                            <ErrorMessage
                              name='paymentMethod'
                              component='div'
                              className='error-text'
                            />
                          </>)}

                          {values.paymentMethod === 'payemandate' &&
                            paymentRule.emandateTypes && (
                              <div className='emandate-options-wrapper'>
                                <Form.Label>
                                  Create New Autopay With
                                </Form.Label>

                                <div className='emandate-options'>
                                  {paymentRule.emandateTypes.includes('withMandateUPI') && (
                                    <label className='radio-pill'>
                                      <Field
                                        type='radio'
                                        name='emandateType'
                                        value='withMandateUPI'
                                      />
                                      <span className='pill-text'>UPI</span>
                                    </label>
                                  )}

                                  {paymentRule.emandateTypes.includes('withMandate') && (
                                    <label className='radio-pill'>
                                      <Field
                                        type='radio'
                                        name='emandateType'
                                        value='withMandate'
                                      />
                                      <span className='pill-text'>
                                        Net Banking / Debit Card
                                      </span>
                                    </label>
                                  )}
                                </div>

                                <ErrorMessage
                                  name='emandateType'
                                  component='div'
                                  className='error-text'
                                />
                              </div>
                            )}
                        </Form.Group>


                        {allocationMode === 'single' &&
                          values.paymentMethod !== 'paymanual' && (
                            <Form.Group className='sip-field coupon-field'>
                              <Form.Label className='coupon-label'>
                                Coupon Code
                              </Form.Label>

                              {!values.couponApplied &&
                                couponErrors.length === 0 && (
                                  <div
                                    className='coupon-card premium'
                                    onClick={() => setShowCouponModal(true)}
                                  >
                                    <div className='coupon-card-left'>
                                      <div className='coupon-neutral-icon'>
                                        <svg
                                          width='18'
                                          height='18'
                                          viewBox='0 0 24 24'
                                          fill='none'
                                        >
                                          <path
                                            d='M20 13L11 22L2 13V4H11L20 13Z'
                                            stroke='currentColor'
                                            strokeWidth='1.5'
                                          />
                                          <circle
                                            cx='7'
                                            cy='7'
                                            r='1.5'
                                            fill='currentColor'
                                          />
                                        </svg>
                                      </div>
                                      <div>
                                        <div className='coupon-title'>
                                          Apply a coupon to your SIP
                                          {/* View available offers for this SIP */}
                                        </div>
                                        {/* <div className="coupon-subtext">
                                      Enter or select a valid coupon to unlock applicable benefits.
                                    </div> */}
                                      </div>
                                    </div>

                                    <div className='coupon-chevron'>→</div>
                                  </div>
                                )}

                              {values.couponApplied && (
                                <div className='coupon-success-minimal'>
                                  <span className='success-icon'>✓</span>
                                  <span className='coupon-success-message'>
                                    Congratulations! Coupon{' '}
                                    <strong>{values.couponName}</strong> applied
                                    to your SIP!
                                  </span>

                                  <button
                                    type='button'
                                    className='remove-coupon'
                                    onClick={() =>
                                      setShowRemoveCouponModal(true)
                                    }
                                  >
                                    Remove
                                  </button>
                                </div>
                              )}

                              {/* {!values.couponApplied &&
                                couponErrors.length > 0 && (
                                  <div className='coupon-error-card refined'>
                                    <div className='coupon-error-header'>
                                      <span className='error-icon'>⚠</span>
                                      <div>
                                        <div className='coupon-title error-text-strong'>
                                          This coupon doesn’t apply to your SIP.
                                        </div>
                                        <div className="coupon-subtext">
                                          Check the conditions below and adjust your SIP if needed.
                                        </div>
                                      </div>
                                    </div>

                                    <ul className='coupon-error-list refined'>
                                      {couponErrors.map((err, index) => (
                                        <li key={index}>{err}</li>
                                      ))}
                                    </ul>

                                    <div className='coupon-error-actions'>
                                      <button
                                        type='button'
                                        className='coupon-action-primary'
                                        onClick={() => {
                                          setCouponErrors([]);
                                          setShowCouponModal(true);
                                        }}
                                      >
                                        Try Another Coupons
                                      </button>

                                      <button
                                        type='button'
                                        className='coupon-action-muted'
                                        onClick={() => {
                                          setCouponErrors([]);
                                          setFieldValue('couponName', '');
                                        }}
                                      >
                                        Dismiss
                                      </button>
                                    </div>
                                  </div>
                                )} */}

                              {!values.couponApplied && couponErrors.length > 0 && (
                                <div className='coupon-error-card refined'>
                                  <div className='coupon-error-header'>
                                    <span className='error-icon'>⚠</span>
                                    <div>
                                      <div className='coupon-title error-text-strong'>
                                        {couponErrors.length === 1
                                          ? couponErrors[0]                            // e.g. "Coupon Not Found"
                                          : "This coupon doesn't apply to your SIP."
                                        }
                                      </div>
                                      {couponErrors.length > 1 && (
                                        <div className="coupon-subtext">
                                          Check the conditions below and adjust your SIP if needed.
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {couponErrors.length > 1 && (
                                    <ul className='coupon-error-list refined'>
                                      {couponErrors.map((err, index) => (
                                        <li key={index}>{err}</li>
                                      ))}
                                    </ul>
                                  )}

                                  <div className='coupon-error-actions'>
                                    <button
                                      type='button'
                                      className='coupon-action-primary'
                                      onClick={() => {
                                        setCouponErrors([]);
                                        setShowCouponModal(true);
                                      }}
                                    >
                                      Try Another Coupon
                                    </button>

                                    <button
                                      type='button'
                                      className='coupon-action-muted'
                                      onClick={() => {
                                        setCouponErrors([]);
                                        setFieldValue('couponName', '');
                                      }}
                                    >
                                      Dismiss
                                    </button>
                                  </div>
                                </div>
                              )}
                            </Form.Group>
                          )}

                        <div className='checkbox-separator' />

                        <Form.Group className='sip-field checkbox-group'>
                          {values.paymentMethod !== "paymanual" &&
                            <>
                              <label className='checkbox-label'>
                                <Field
                                  type='checkbox'
                                  name='autoRenew'
                                />

                                <span className='checkbox-text'>
                                  Enable auto-renew for this SIP
                                  <span className='info-tooltip inline'>
                                    ⓘ
                                    <span className='tooltip-content'>
                                      Checking this option will create a Autopay OR
                                      Mandate for double the duration of SIP you are
                                      choosing and once your SIP expires, a new SIP
                                      will be created for you. You will have an
                                      option to uncheck this later on if you do not
                                      want a renewal.
                                    </span>
                                  </span>
                                </span>
                              </label>
                            </>}


                          <label className='checkbox-label terms-checkbox'>
                            <Field type='checkbox' name='acceptTerms' />
                            <span className='checkbox-text'>
                              By creating this SIP, you agree to the{' '}
                              <strong>
                                <a
                                  href='https://www.ebullion.in/terms-and-conditions/terms.html'
                                  target='_blank'
                                  className='terms-link'
                                >
                                  Terms and Conditions
                                </a>
                              </strong>
                            </span>
                          </label>
                          <ErrorMessage
                            name='acceptTerms'
                            component='div'
                            className='error-text'
                          />
                        </Form.Group>

                        <Button type='submit' className='sip-btn'>
                          Confirm & Create SIP
                        </Button>
                      </div>
                    )}

                    <CouponModal
                      show={showCouponModal}
                      onClose={() => setShowCouponModal(false)}
                      couponList={coupons}
                      onApply={(code) => {
                        handleValidateCoupon(code, values, setFieldValue);
                        setShowCouponModal(false);
                      }}
                    />
                    <ConfirmActionModal
                      show={showRemoveCouponModal}
                      onCancel={() => setShowRemoveCouponModal(false)}
                      onConfirm={() => {
                        setFieldValue('couponApplied', false);
                        setFieldValue('couponName', '');
                        setCouponErrors([]);
                        setShowRemoveCouponModal(false);
                      }}
                      title='Remove Coupon?'
                      description={
                        <>
                          Are you sure you want to remove{' '}
                          <strong>{values.couponName}</strong>?
                          <br />
                          This offer will no longer be applied to your SIP.
                        </>
                      }
                      confirmText='Remove Coupon'
                      intent='danger'
                    />

                    {finalPayload && (
                      <SipSummaryModal
                        show={showSummary}
                        values={finalPayload}
                        allocationMode={allocationMode}
                        goldPercent={goldPercent}
                        onClose={() => setShowSummary(false)}
                        onProceed={handleRedirectToPayment}
                      />
                    )}
                  </Form>
                </>
              );
            }}
          </Formik>
        </Card>
      </Container>
      <div className='delivery-faq-section'>
        <FAQAccordion faqs={FAQS} />
      </div>
    </>
  );
};

export default CreateSip;
